import React, { useEffect, useRef, useState } from "react";
import { Card, Button as ButtonAntd, Form, Input, Tooltip } from "antd";
import Draggable from "react-draggable";
import { CloseCircleOutlined, CloudDownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { emitCustomEvent, useCustomEventListener } from "react-custom-events";
import { MathUtils, Vector3 } from "three";
import { AoiSaveContainer } from "./aoi-save-modal.styles";
import { getMeasurementsArea } from "../../../common/potreeUtils";
import { convertPointsArrToWkt, isSelfIntersecting } from "../../../common/gisUtils";
import { message } from "antd";
import { getVisibleClasses } from "../../../common/LASDataClassification";
import { exportQuickLAS, getVisibleData } from "../../../common/lasExportUtils";
import { getIdToken } from "../../userManager/userManagementUtils";
import { AOIAddState } from "../../../types/interfaces";
import CustomMessage from "../../common/CustomMessage";

interface ExportModalProps {
    onCancel: () => void;
    setAoiAddState: (b: null | AOIAddState) => void;
    aoiAddState: AOIAddState;
}

export default function AoiSaveModal({ onCancel, setAoiAddState, aoiAddState }: ExportModalProps) {
    let mousePos = window.potreeViewer.inputHandler.mouse;
    const form = Form.useForm()[0];
    const [addLoader, setAddLoader] = useState(false);
    const [downloadLoader, setDownloadLoader] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const [showMessage, setShowMessage] = useState<boolean>(false);

    useEffect(() => {
        // Get total offset of the card
        const card = cardRef.current;
        if (card) {
            const rect = card.parentElement?.getBoundingClientRect();
            let left = 0;
            if(rect){
                left = rect.left;
                card.style.transform = `translate(${mousePos.x - left}px, ${mousePos.y - card.clientHeight}px)`;
                card.style.right = "0px"
            }
            const cardRect = card.getBoundingClientRect();
            const cardLeft = cardRect.left;
            const cardRight = cardRect.right;
            const cardTop = cardRect.top;
            if (cardLeft < 0){
                const newLeft = left + cardLeft - 10
                card.style.transform = `translate(${mousePos.x - newLeft}px, ${mousePos.y - card.clientHeight}px)`;
            }
            if(cardRight > window.innerWidth){
                const newLeft = left + (cardRight - window.innerWidth) + 10
                card.style.transform = `translate(${mousePos.x - newLeft}px, ${mousePos.y - card.clientHeight}px)`;
            }
            if(cardTop < 0){
                const newTop = card.clientHeight + cardTop - 10
                card.style.transform = `translate(${mousePos.x - left}px, ${mousePos.y - newTop}px)`;
            }
        }
    }, []);

    const cardStyle1: React.CSSProperties = {
        position: "absolute",
        width: 320,
        top: 10,
        right: 500,
        color: "white",
        background: "#303336cc"
    };

    const _initialValues = {
        name: "",
        format: "LAS",
        resolution: -1
    };

    const get3dPoints = (data: any): boolean | [number, number, number][] => {
        // Polygon validation
        const measure = data.measurements.slice(-1)[0];
        const points = measure.points;

        if (points.length < 3) {
            message.error("Please draw a polygon with at least 3 points");
            return false;
        }
        const isIntersecting = isSelfIntersecting([...points, points[0]]);

        if (isIntersecting) {
            message.error("Please draw a non-self-intersecting polygon");
            return false;
        }
        return points;
    }

    const onSave = () => {
        const formData = form.getFieldsValue();
        const data = window.potreeViewer.saveProject(window.potreeViewer);
        const points2d = get3dPoints(data);
        if (!points2d) {
            return;
        }

        const uuid = MathUtils.generateUUID();
        const { area, areaText } = getMeasurementsArea(window.potreeViewer.scene.measurements);

        // Save AOI
        setAddLoader(true)
        emitCustomEvent("saveAoi", {
            uuid: uuid,
            name: formData.name,
            epsg: window.activeProjection?.getCRS() || "-",
            area: area,
            areaText: areaText,
            measurements: data.measurements.slice(-1),
            volumes: data.volumes
        });
    };

    useCustomEventListener("aoiSaved", (data : any) => {
        if(data.success){
            setAddLoader(false)
            setAoiAddState(null)
            message.success("AOI saved successfully")
        }else {
            setAddLoader(false)
            message.error(data.error || "Failed to save AOI")
        }
    })

    const onDownload = () => {
        {showMessage && (
            <CustomMessage
                content="Left click to select points. Right click when finished."
                duration={10}
                onClose={() => setShowMessage(false)}
            />
        )}

        const formData = form.getFieldsValue();

        const name = formData.name;
        if (!name) {
            message.error("Please enter a name");
            return;
        }

        const CRS = window.activeProjection?.getCRS();

        // if (!CRS) {
        //     message.error("Active projection not found");
        //     return;
        // }

        const points2d = get3dPoints(window.potreeViewer.saveProject(window.potreeViewer));

        if (typeof points2d === "boolean") {
            return;
        }

        const polygon = convertPointsArrToWkt(points2d);

        const classification= getVisibleClasses()
        const classificationIndices = classification ? classification.map((c : any) => c.value) : [];
        const visibleData = getVisibleData();

        const data_ids : any = {}

        Object.keys(visibleData).forEach((key, dataIndex) => (
            data_ids[key] = visibleData[key].map((item : any) => {
                return item.data_id
            })
        ))

        setDownloadLoader(true)

        getIdToken().then((idToken) => {
            const payload = {
                download_name: name,
                polygon: polygon,
                data_ids: data_ids,
                classification: classificationIndices?.length > 0 ? classificationIndices : -1,
                process: "download",
                token: idToken,
                srs_override: CRS
            };
            exportQuickLAS(payload).then(() => {
                setDownloadLoader(false)
            }).catch(() => {
                setDownloadLoader(false)
            })
        }).catch(() => {
            message.error("Failed to get token")
            setDownloadLoader(false)
        })
    };
    const title = aoiAddState.isQuickDownload ? "Quick Download" : "Save AOI";

    return (
        <AoiSaveContainer>
            <Draggable axis="none">
                <Card onContextMenu={(event) => { event.preventDefault() }} ref={cardRef} title={title} extra={<CloseCircleOutlined onClick={onCancel} />} style={cardStyle1}>
                    <Form
                        form={form}
                        name="aoiSaveForm"
                        initialValues={_initialValues}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Form.Item name="name" label="Name">
                            <Input />
                        </Form.Item>
                        <Form.Item style={{ textAlign: "right", marginTop: "5px" }}>
                            {
                                !aoiAddState.isQuickDownload && (
                                    <ButtonAntd
                                        type="primary"
                                        onClick={() => onSave()}
                                        loading={addLoader}
                                        style={{ marginRight: 10, display: "flex" }}
                                    >
                                        Save
                                    </ButtonAntd>
                                )
                            }
                            <ButtonAntd onClick={onCancel} style={{ marginRight: 10 }}>
                                Close
                            </ButtonAntd>
                            {
                                aoiAddState.isQuickDownload && (
                                    <ButtonAntd type="primary" onClick={onDownload} loading={downloadLoader} style={{ marginRight: 10, display: "flex", alignItems: "center" }}>
                                        {!downloadLoader && <CloudDownloadOutlined />}
                                        Quick Download
                                    </ButtonAntd>
                                )
                            }
                            <Tooltip title="Processes a small download quickly, and delivers to “Downloads”">
                                <InfoCircleOutlined style={{ color: '#ffffff', cursor: "pointer" }}/>
                            </Tooltip>
                        </Form.Item>
                    </Form>
                </Card>
            </Draggable>
        </AoiSaveContainer>
    );
}
